import NatDisasterRepo from './repository/NatDisasterRepo'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import React, {Component, ReactElement, ReactNode} from 'react'
import EventEmitterExResident from '../../../components/shared/pageBaseComponents/EventEmitterExResident'
import {Nullable} from '../../../app/TypeHelpers'
import {DisasterPreventionsInfoDetail} from './repository/NatGetDisasterPreventionInfoForResident'
import {PlainBackLink} from '../../../styledComponents/Links'
import {BlockSpacer} from '../../../styledComponents/ResponsiveConvenience'
import {StyledDiv, StyledH3, StyledSpan} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {Colors, FontSize, FontSizeInPixel, FontWeights} from '../../../styledComponents/Constants'
import {FullWidthLeftRowFlexContainer, LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {formatDateSlashSeparator} from '../../../formatters/formatDate'
import {DISASTER_RELEASE_SETTING} from '../../../CodeMaster'
import {DisplayBlockDiv} from '../../../styledComponents/DisplayItem'
import {StyledSecondaryButton} from '../../../styledComponents/Button'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faFilePdf} from '@fortawesome/free-solid-svg-icons'
import withStyles from '@mui/styles/withStyles'
import {StyledLink} from '../../../styledComponents/adminPage/Links'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    disasterPreventionId: number
    disasterRepo: NatDisasterRepo
    location: Location
    eventEmitter: EventEmitterExResident
}

interface State {
    disasterInfo: DisasterPreventionsInfoDetail,
    isLoadCompleted: boolean
}

class NatResidentDisasterPreventionsDetailsPage
    extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)
        this.state = {
            disasterInfo: {
                disasterPreventionId: 0,
                title: '',
                description: '',
                releaseSetting: '',
                updateDatetime: '',
                disasterPreventionFile: null
            },
            isLoadCompleted: false
        }

        this.handleBackButtonClick = this.handleBackButtonClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const {condominiumCd, disasterPreventionId, disasterRepo, eventEmitter} = this.props

            const response = await this.props.asyncCallFunction(
                disasterRepo.getDisasterInfoForResident.bind(disasterRepo, condominiumCd, disasterPreventionId),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )
            if (response?.details) {
                this.setState({
                    disasterInfo: response.details
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    handleBackButtonClick(): void {
        this.props.navigate(`/resident/condominiums/${this.props.condominiumCd}/disasters/preventions`)
    }

    renderDisasterPreventionInfo(): ReactNode {
        const {disasterInfo} = this.state
        return (
            <MainContainer>
                <LeftRowFlexContainer>
                    <UpdateDateTime>
                        {formatDateSlashSeparator(disasterInfo.updateDatetime)}
                    </UpdateDateTime>
                    <BlockSpacer ml={'8px'}/>
                    {(disasterInfo.releaseSetting === DISASTER_RELEASE_SETTING.DISASTER.value) &&
                        <ReleaseSettingLabel>
                            {DISASTER_RELEASE_SETTING.codeToEnumConstants(disasterInfo.releaseSetting).label}
                        </ReleaseSettingLabel>
                    }
                </LeftRowFlexContainer>
                <DisasterInfoTitle>{disasterInfo.title}</DisasterInfoTitle>
                <DisasterInfoDescription dangerouslySetInnerHTML={{__html: disasterInfo.description}}/>
                <AttachmentFileArea>
                {(disasterInfo.disasterPreventionFile?.map((disasterPreventionFile, index) =>
                    <AttachmentFile key={`disasterPreventionFile_${index}`}>
                        <AttachmentFileLink to={disasterPreventionFile.path} target={'_blank'}>
                            <FullWidthLeftRowFlexContainer>
                                <FontAwesomeIcon icon={faFilePdf} color={'#FF6378'} size={'lg'}/>
                                <AttachmentFileName>{disasterPreventionFile.publishMaterialName}</AttachmentFileName>
                            </FullWidthLeftRowFlexContainer>
                        </AttachmentFileLink>
                    </AttachmentFile>
                ))}
                </AttachmentFileArea>
            </MainContainer>
        )
    }


    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return <>
            <PlainBackLink to={`/resident/condominiums/${this.props.condominiumCd}/disasters/preventions`}>
                戻る
            </PlainBackLink>
            <BlockSpacer height={'16px'}/>
            {this.renderDisasterPreventionInfo()}
            <BlockSpacer height={'15px'}/>
            <DisplayBlockDiv>
                <StyledSecondaryButton onClick={this.handleBackButtonClick}>一覧へ戻る</StyledSecondaryButton>
            </DisplayBlockDiv>
        </>
    }

}

export default createResidentWithErrorHandlingPage(NatResidentDisasterPreventionsDetailsPage)

const MainContainer: StyledDiv = styled.div`
    background-color: ${Colors.Surface};
    padding: 15px 15px 30px;
`

const UpdateDateTime: StyledDiv = styled.div`
    color: ${Colors.AccentDark};
    font-size: ${FontSizeInPixel.Small}px;
`

const ReleaseSettingLabel = styled.div`
    background-color: ${Colors.Disaster};
    color: ${Colors.Surface};
    text-align: center;
    font-size: 8px;
    line-height: 8px;
    padding: 4px 3px;
`

const DisasterInfoTitle: StyledH3 = styled.h3`
    font-size: ${FontSize.Accent};
    font-weight: ${FontWeights.Bold};
    margin: 20px 0 20px 0;
    word-break: break-all;
`

const DisasterInfoDescription: StyledSpan = styled.span`
    font-size: ${FontSize.Middle};
    font-weight: ${FontWeights.Normal};
    line-height: 21px;
    word-break: break-all;
`

const AttachmentFileArea = styled.div`
    > div:first-child {
        margin-top: 20px;
    }
`

const AttachmentFile = styled.div`
    border: 1px solid #D9D9D9;
    border-radius: 4px;
    margin-top: 5px;
`

const AttachmentFileName = styled.span`
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding-left: 10px;
    padding-right: 10px;
    width: 100%;
`

const AttachmentFileLink = withStyles({
    root: {
        color: Colors.Body,
        fontSize: FontSizeInPixel.Body,
        lineHeight: 1.5,
        padding: '20px 10px',
        display: 'block',
    },
})(StyledLink)